import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subscription,
  throwError,
} from 'rxjs';
import {
  publishReplay,
  refCount,
  take,
  switchMap,
  map,
  catchError,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CustomEncoder } from '../auth/helpers/customEncoder';
import { AuthenticationService } from '../auth/services/authentication.service';
import {
  PlaceInfo,
  Places,
  PlacesetID,
  PlaceSetResponse,
  SavedFavoriteResponse,
} from '../_models/pcmws/favoritePlaces/favoritePlacesResponse';
import { PlaceSetType, UIPlace } from '../_models/state/placeSetState';
import { AlertService } from '../auth/services/alert.service';
import { SingleSearchLocation } from '../_models/pcmws/singleSearch/singleSearchResponse';
import { UIPlaceState } from '../_models/state/placeState';
import { StopLocation } from '../_models/state/stopState';
import { Coords } from '../_models/common/coords';

@Injectable()
export class PlaceSetService {
  private favoritePlaceStore: BehaviorSubject<UIPlaceState[]> =
    new BehaviorSubject<UIPlaceState[]>(null);
  readonly favoritePlaceStore$: Observable<UIPlaceState[]> =
    this.favoritePlaceStore.asObservable();

  private favoritePlaceSets: number[] = [];

  private get favoritePlaceStoreValue(): UIPlaceState[] {
    return this.favoritePlaceStore.getValue();
  }

  private setFavoritePlaceStore(newStore: UIPlaceState[]) {
    this.favoritePlaceStore.next(newStore);
  }
  // custom Places
  private customPlaceStore: BehaviorSubject<UIPlaceState[]> =
    new BehaviorSubject<UIPlaceState[]>(null);

  readonly customPlaceStore$: Observable<UIPlaceState[]> =
    this.customPlaceStore.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private alertService: AlertService
  ) {
    this.getFavoritePlacesList();
    this.getCustomPlaces();
  }
  addPlaceSetName(isShared: boolean): Observable<PlacesetID> {
    const queryParam = new HttpParams().set('useShared', isShared);
    const postData = {
      Name: this.getSetName(isShared),
      Style: null,
      Options: {
        DisplayOnMap: true,
        Searchable: true,
        Alertable: true,
      },
    };
    return this.http.post<PlacesetID>(
      `${environment.daUrl}place-sets?${queryParam}`,
      postData
    );
  }
  getFavoritePlaceSetName(isShared: boolean): Observable<PlaceSetResponse[]> {
    const queryParam = this.getQueryParam(isShared, false);
    return this.http.get<PlaceSetResponse[]>(
      `${environment.daUrl}place-sets/-1?${queryParam}`
    );
  }
  private getFavoritePlaceSet(
    queryParam: HttpParams
  ): Observable<PlaceSetResponse[]> {
    // using JSON.parse() to convert string to boolean
    const shared = JSON.parse(queryParam.get('useShared'));
    return this.getFavoritePlaceSetName(shared).pipe(
      switchMap((placeSet: PlaceSetResponse[]) => {
        if (placeSet?.length && placeSet[0]?.Id) {
          this.favoritePlaceSets.push(placeSet[0]?.Id);
          return this.getFavoritePlaces(placeSet[0]?.Id);
        }
        return of([]);
      })
    );
  }

  private getFavoritePlaces(setId: number): Observable<PlaceSetResponse[]> {
    const queryParam = new HttpParams()
      .set('includePlaces', true)
      .set('onlyMine', false)
      .set('placeNameFilter', '')
      .set('readOnlyPublicData', false)
      .set('useShared', true);

    return this.http.get<PlaceSetResponse[]>(
      `${environment.daUrl}place-sets/${setId}?${queryParam}`
    );
  }

  private getCombinedFavoritePlaceSet(): Observable<
    [PlaceSetResponse[], PlaceSetResponse[]]
  > {
    // shared true, include place false
    const sharedQueryParam = this.getQueryParam(true, false);
    // shared false, include place false
    const privateQueryParam = this.getQueryParam(false, false);

    return forkJoin([
      this.getFavoritePlaceSet(privateQueryParam).pipe(
        map((resp) => {
          resp.forEach((p) => {
            p.Places.forEach((place) => {
              place.IsShared = false;
            });
          });
          return resp;
        })
      ),
      this.getFavoritePlaceSet(sharedQueryParam).pipe(
        map((resp) => {
          resp.forEach((p) => {
            p.Places.forEach((place) => {
              place.IsShared = true;
            });
          });
          return resp;
        })
      ),
    ]);
  }

  private retrieveCombinedFavoritePlaces(data: PlaceSetResponse): UIPlace {
    const places = {
      Id: data?.Id,
      PlaceName: data?.Name,
      PlaceCounts: data?.PlaceCounts,
      Places: data?.Places.map((place) => {
        return this.mapPlaceValue(place);
      }),
    };
    return places;
  }
  private mapPlaceValue(place: Places, isShared?: boolean) {
    if (typeof isShared !== 'undefined') {
      place.IsShared = isShared;
    }
    place.Type = PlaceSetType.Favorite;
    place['FullAddress'] = [
      place?.Address?.StreetAddress,
      place?.Address?.City,
      place?.Address?.State,
      place?.Address?.Zip,
    ]
      .filter((val) => Boolean(val))
      .join(', ');
    place.DisplayText = [
      place?.PlaceName,
      place?.FullAddress,
      place?.Address?.Country,
      place?.Address?.County,
    ]
      .filter((val) => Boolean(val))
      .join(' ');
    return place;
  }
  private getCombinedFavoritePlaces(
    sources: [PlaceSetResponse[], PlaceSetResponse[]]
  ): UIPlaceState[] {
    let placesBatch1: UIPlace;
    let placesBatch2: UIPlace;
    if (sources[0][0]) {
      placesBatch2 = this.retrieveCombinedFavoritePlaces(sources[0][0]);
    }
    if (sources[1][0]) {
      placesBatch1 = this.retrieveCombinedFavoritePlaces(sources[1][0]);
    }

    let result: UIPlaceState[] = [];
    if (placesBatch1?.Places && placesBatch2?.Places) {
      result = placesBatch2.Places.concat(placesBatch1.Places).reduce(
        (accumulator, value) => accumulator.concat(value),
        []
      );
    } else if (placesBatch1?.Places) {
      result = placesBatch1.Places;
    } else if (placesBatch2?.Places) {
      result = placesBatch2.Places;
    }
    return result.sort((place1, place2) =>
      place1.PlaceName.toLowerCase() > place2.PlaceName.toLowerCase() ? 1 : -1
    );
  }

  getFavoritePlacesList(): Subscription {
    return this.getCombinedFavoritePlaceSet()
      .pipe(
        switchMap((sources) => {
          return of(this.getCombinedFavoritePlaces(sources));
        }),
        take(1)
      )
      .subscribe((list: UIPlaceState[]) => {
        this.setFavoritePlaceStore(list);
      });
  }

  saveNewPlace(
    stopLocation: StopLocation,
    placeInfo: PlaceInfo
  ): Observable<UIPlaceState> {
    let placePostData: Places = this.getPlacePostData(stopLocation, placeInfo);
    return this.http
      .post<SavedFavoriteResponse>(`${environment.daUrl}places`, placePostData)
      .pipe(
        switchMap((savedFavoriteResponse: SavedFavoriteResponse) => {
          placePostData = this.mapPlaceValue(placePostData, placeInfo.shared);
          placePostData = { ...placePostData, ...savedFavoriteResponse };
          const savedPlace: UIPlaceState = placePostData as UIPlaceState;
          let favoritePlace = this.favoritePlaceStoreValue;
          favoritePlace = [...favoritePlace, placePostData];
          this.setFavoritePlaceStore(favoritePlace);
          return of(savedPlace);
        })
      );
  }
  getPlacePostData(place: StopLocation, placeInfo: PlaceInfo): Places {
    const address = {
      StreetAddress: place.address.streetAddress,
      City: place.address.city,
      State: place.address.stateAbbreviation.toUpperCase(), //State abbreviations should be capitalized
      Zip: place.address.zip,
      County: place.address.county,
    };
    const coords = {
      Lat: place.coords[1],
      Lon: place.coords[0],
    };

    const postData = {
      Address: address,
      CategoryId: placeInfo.categoryId,
      Coords: coords,
      PlaceName: placeInfo.name,
      SetId: placeInfo.setId,
      CompanyId: placeInfo?.companyId,
      Phone: placeInfo?.phone,
      Info: placeInfo?.info,
    };

    return postData;
  }

  private getCustomPlaceSets(
    queryParam: HttpParams
  ): Observable<PlaceSetResponse[]> {
    return this.http
      .get<PlaceSetResponse[]>(
        `${environment.daUrl}place-sets/-1?${queryParam}`
      )
      .pipe(publishReplay(1), refCount(), take(1));
  }

  getCustomPlaces() {
    //shared true and included places true
    const sharedQueryParam = this.getQueryParam(true, true);
    //shared false and included places true
    const privateQueryParam = this.getQueryParam(false, true);
    const sharedCustomPlaceRequest = this.getCustomPlaceSets(sharedQueryParam);
    const privateCustomPlaceRequest =
      this.getCustomPlaceSets(privateQueryParam);
    return forkJoin([sharedCustomPlaceRequest, privateCustomPlaceRequest])
      .pipe(take(1))
      .subscribe((customPlaces: [PlaceSetResponse[], PlaceSetResponse[]]) => {
        this.customPlaceStore.next(this.getCustomPlaceSetState(customPlaces));
      });
  }
  getCustomPlaceSetState(
    customPlaces: [PlaceSetResponse[], PlaceSetResponse[]]
  ) {
    // Filter out the favorite locations;
    const customPlaceResults: PlaceSetResponse[] = customPlaces[0]
      .concat(customPlaces[1])
      .filter((place) => !this.favoritePlaceSets.includes(place.Id));

    return this.retrieveCustomPlaceSetState(customPlaceResults);
  }
  private retrieveCustomPlaceSetState(
    customPlaceResults: PlaceSetResponse[]
  ): UIPlace[] {
    return customPlaceResults.map((placeResult) => {
      return {
        Id: placeResult?.Id,
        Icon: placeResult?.Categories?.find((value) => value.Icon)
          ? `assets/img/poi-icons/poi_${
              placeResult?.Categories?.find((value) => value.Icon)?.Icon
            }.png`
          : null,
        IconURL: placeResult?.Categories?.find((value) => value.IconURL)
          ?.IconURL,
        PlaceName: placeResult?.Name,
        PlaceCounts: placeResult?.PlaceCounts,
        Places: placeResult?.Places.map((place) => {
          place.Type = PlaceSetType.Custom;
          place.DisplayText = [
            place?.PlaceName,
            place?.Address?.City,
            place?.Address?.State,
            place?.Address?.StreetAddress,
            place?.Address?.Zip,
            place?.Address?.Country,
            place?.Address?.County,
          ]
            .filter((val) => Boolean(val))
            .join(' ');
          return place;
        }),
      };
    });
  }

  deletePlace(id: number) {
    return this.http
      .delete<PlacesetID>(`${environment.daUrl}places/${id}`)
      .pipe(
        map(() => {
          this.setFavoritePlaceStore(
            this.favoritePlaceStoreValue.filter((r) => r.Id !== id)
          );
        }),
        catchError((err) => {
          this.alertService.error('Issue with Delete Favorite Place');
          return throwError(err);
        })
      );
  }
  checkPlaceCoordsBelongsToFavorite(
    location: SingleSearchLocation
  ): Observable<StopLocation> {
    let id, favourite_name;
    return this.favoritePlaceStore$.pipe(
      map((placeSets: UIPlaceState[]) => {
        const index = placeSets?.findIndex((placeSet) => {
          const place_coords: Coords = {
            Lat: placeSet.Coords.Lat.toString(),
            Lon: placeSet.Coords.Lon.toString(),
          };
          if (
            JSON.stringify(place_coords) === JSON.stringify(location?.Coords)
          ) {
            id = placeSet.Id;
            favourite_name = placeSet.PlaceName;
            return true;
          }
          return false;
        });
        return {
          name: favourite_name,
          favoriteId: id,
          address: {
            streetAddress: location.Address.StreetAddress,
            city: location.Address.City,
            stateAbbreviation: location.Address.State,
            zip: location.Address.Zip,
            county: location.Address.County,
            countryAbbreviation: location.Address.Country,
          },
          coords: [
            Number.parseFloat(location.Coords.Lon),
            Number.parseFloat(location.Coords.Lat),
          ],
          isFavorite: index >= 0,
        };
      })
    );
  }
  getSetName(isShared: boolean) {
    const privateSetName = `${this.authService?.currentUserValue?.account} - ${this.authService?.currentUserValue?.userName}`;
    return isShared
      ? this.authService?.currentUserValue?.account
      : privateSetName;
  }
  getQueryParam(isShared: boolean, isPlaceIncluded: boolean) {
    const setName = this.getSetName(isShared);
    const queryParam = new HttpParams({ encoder: new CustomEncoder() })
      .set('includePlaces', isPlaceIncluded)
      .set('onlyMine', true)
      .set('readOnlyPublicData', false)
      .set('useShared', isShared);

    return !isPlaceIncluded ? queryParam.set('name', setName) : queryParam;
  }
}
